import { trpc } from '../../api/trpc';
import { HomeGreeting } from './HomeGreeting';
import { HomeFeedSection } from './HomeFeedSection';
import { HomeFeedCard } from './HomeFeedCard';
import { HomeSkeleton } from './HomeSkeleton';
import { HomeAllCaughtUp } from './HomeAllCaughtUp';
import { CadeAvatar } from '../CadeAvatar';
import type { HomeFeedItem } from '@cade/shared-types';

interface HomeScreenProps {
  onNavigate: (conversationId: string) => void;
  onNewConversation: (text: string, fileIds?: string[]) => void;
}

export function HomeScreen({ onNavigate, onNewConversation }: HomeScreenProps) {
  const feed = trpc.home.getFeed.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  if (feed.isLoading) return <HomeSkeleton />;

  const data = feed.data;
  const hasFeedItems = data && (data.attention.length > 0 || data.away.length > 0);

  // Empty state or error: show personalized hero + suggestion chip rail.
  // The chip rail surfaces the day-one Entra posture suggestion when the
  // tenant has the connector and no prior posture assessment exists.
  if (!hasFeedItems) {
    // Extract first name from greeting like "Good evening, Omri"
    const greeting = data?.greeting ?? '';
    const firstName = greeting.includes(',') ? greeting.split(',').pop()?.trim() : '';

    return (
      <div className="home-screen home-screen--hero">
        {firstName && (
          <div className="home-hero">
            <CadeAvatar size={72} />
            <h1 className="home-hero-greeting">Hi, {firstName}!</h1>
          </div>
        )}
        <HomeAllCaughtUp onSuggestion={(text) => onNewConversation(text)} />
      </div>
    );
  }

  function handleNavigate(resourceId: string, resourceType: string) {
    if (resourceType === 'conversation') {
      onNavigate(resourceId);
    }
  }

  return (
    <div className="home-screen">
      <HomeGreeting greeting={data.greeting} pendingCount={data.pendingCount} />

      <HomeFeedSection title="Needs your attention" itemCount={data.attention.length}>
        {data.attention.map((item: HomeFeedItem) => (
          <HomeFeedCard key={item.id} item={item} onNavigate={handleNavigate} />
        ))}
      </HomeFeedSection>

      <HomeFeedSection title="While you were away" itemCount={data.away.length}>
        {data.away.map((item: HomeFeedItem) => (
          <HomeFeedCard key={item.id} item={item} onNavigate={handleNavigate} />
        ))}
      </HomeFeedSection>
    </div>
  );
}
